import React from 'react';
import { MonitorUp, RotateCw, Tablet, LogOut, ArrowLeft } from 'lucide-react';
import { useSupportedWorkspaceViewport } from '../../hooks/useSupportedWorkspaceViewport';
import { resolveTenantProductName } from '../../lib/whiteLabelBranding';

interface WorkspaceViewportGateProps {
  children: React.ReactNode;
  onSignOut: () => void;
  onNavigateHome: () => void;
  // If the app has tenant branding, it could be passed here, but we keep it simple as requested
}

export const WorkspaceViewportGate: React.FC<WorkspaceViewportGateProps> = ({
  children,
  onSignOut,
  onNavigateHome,
}) => {
  const { isSupported, isPortraitTablet } = useSupportedWorkspaceViewport();
  const productName = resolveTenantProductName();

  if (isSupported) {
    return <>{children}</>;
  }

  return (
    <main className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0b1220] px-6 text-slate-200 overflow-y-auto">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg h-96 bg-sky-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center text-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-sky-400/40 bg-sky-400/10 text-sky-300 shadow-[0_0_28px_rgba(56,189,248,0.12)]">
          <MonitorUp strokeWidth={1.5} className="h-9 w-9" aria-hidden="true" />
        </div>

        <h1 className="mb-4 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
          <span className="whitespace-nowrap">{productName} needs</span>{' '}
          <span className="block whitespace-nowrap sm:inline">more screen space</span>
        </h1>

        <p className="mb-4 max-w-[390px] text-sm leading-relaxed text-slate-300 sm:text-base">
          For the best experience, open {productName} on a laptop or desktop. If you are using a tablet, try rotating to landscape mode.
        </p>
        
        {isPortraitTablet && (
          <div className="mb-4 flex flex-col items-center gap-2 text-sky-300">
            <div className="relative flex h-12 w-12 items-center justify-center" aria-hidden="true">
              <Tablet className="h-8 w-8" strokeWidth={1.5} />
              <RotateCw className="absolute -right-1 -top-1 h-5 w-5" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Try rotating your tablet to landscape.</p>
          </div>
        )}

        <p className="mb-8 text-sm text-slate-400">
          Your account and trading data are unaffected.
        </p>

        <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onNavigateHome}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sky-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-[#0b1220]"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Home
          </button>
          
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-700 bg-transparent px-6 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-[#0b1220]"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>
    </main>
  );
};
